/**
 * Live themed invoice preview. Mirrors the server-side PDF in `exporters.ts`
 * — same theme ids, same accent color, same logo. Useful for the Create tab
 * and as a read-only render anywhere we want to show an invoice "as it will
 * appear to the recipient".
 */

import { useMemo } from 'react';
import type { Invoice, LineItem, Party } from '@/types/invoice';
import { getTheme, type InvoiceTheme } from '@/lib/themes';

interface PreviewInvoice {
  invoiceNumber: string;
  issueDate?: string | null;
  dueDate?: string | null;
  currency: string;
  vendor: Party;
  buyer: Party;
  lineItems: LineItem[];
  subtotal: number;       // minor units (cents)
  taxTotal: number;
  total: number;
  paymentTerms?: string | null;
  notes?: string | null;
  theme: string;
  accentColor: string;
}

interface Props {
  invoice: PreviewInvoice | Invoice;
  logoUrl?: string | null;
  /** Bounding scale: 1 = natural A4 width (816px @ 96dpi); shrink for thumb previews. */
  scale?: number;
}

const money = (cents: number): string => (cents / 100).toFixed(2);

export function InvoicePreview({ invoice, logoUrl, scale = 1 }: Props) {
  const theme = useMemo(() => getTheme(invoice.theme), [invoice.theme]);
  const accent = invoice.accentColor || theme.accent;
  const muted = theme.mutedInk;
  const rule = theme.rule;

  const containerStyle: React.CSSProperties = {
    width: 816 * scale,
    minHeight: 1056 * scale,
    background: theme.background,
    color: theme.ink,
    fontFamily: theme.fontStack,
    fontSize: 13 * scale,
    lineHeight: 1.4,
    boxShadow: '0 18px 40px -18px rgba(0,0,0,0.25), 0 4px 10px -4px rgba(0,0,0,0.10)',
    borderRadius: 6 * scale,
    overflow: 'hidden',
    margin: '0 auto',
    transformOrigin: 'top center',
  };

  const titleAlign = theme.headerLayout;
  const headerBg = theme.bandStyle === 'solid' ? accent : 'transparent';
  const headerColor = theme.bandStyle === 'solid' ? '#FFFFFF' : theme.ink;
  const headerBorder = theme.bandStyle === 'outline' ? `1px solid ${accent}` : 'none';
  const headerPad = theme.bandStyle === 'solid' ? `${28 * scale}px ${56 * scale}px` : `${20 * scale}px ${56 * scale}px`;

  return (
    <div className="invoice-preview" style={containerStyle}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16 * scale,
          padding: headerPad,
          background: headerBg,
          color: headerColor,
          // Pick exactly ONE border treatment to avoid mixing shorthand/longhand.
          ...(theme.bandStyle === 'minimal'
            ? { borderBottom: `1px solid ${rule}` }
            : theme.bandStyle === 'outline'
              ? { border: headerBorder, margin: `${24 * scale}px ${56 * scale}px 0` }
              : null),
        }}
      >
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Company logo"
            style={{
              maxHeight: 56 * scale,
              maxWidth: 160 * scale,
              objectFit: 'contain',
              flex: '0 0 auto',
              background: 'transparent',
            }}
          />
        )}
        <div style={{ flex: 1, textAlign: titleAlign }}>
          <div
            style={{
              fontFamily: theme.fontTitleStack,
              fontWeight: 700,
              fontSize: 32 * scale,
              letterSpacing: '0.04em',
              lineHeight: 1.1,
            }}
          >
            FACTURE
          </div>
          {invoice.invoiceNumber && (
            <div style={{ marginTop: 4 * scale, opacity: 0.75, fontSize: 12 * scale }}>
              N° {invoice.invoiceNumber}
            </div>
          )}
        </div>
      </div>

      {/* Meta + parties */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 24 * scale,
          padding: `${28 * scale}px ${56 * scale}px ${12 * scale}px`,
        }}
      >
        <div>
          <SectionLabel theme={theme} accent={accent} scale={scale}>Émetteur</SectionLabel>
          <PartyBlock party={invoice.vendor} muted={muted} scale={scale} />
          <div style={{ height: 16 * scale }} />
          <SectionLabel theme={theme} accent={accent} scale={scale}>Facturé à</SectionLabel>
          <PartyBlock party={invoice.buyer} muted={muted} scale={scale} />
        </div>

        <div style={{ textAlign: 'right' }}>
          <MetaPair label="Date d'émission" value={invoice.issueDate ? formatDate(invoice.issueDate) : '—'} theme={theme} accent={accent} muted={muted} scale={scale} />
          <MetaPair label="Date d'échéance" value={invoice.dueDate   ? formatDate(invoice.dueDate)   : '—'} theme={theme} accent={accent} muted={muted} scale={scale} />
          <MetaPair label="Devise"          value={invoice.currency || 'MAD'} theme={theme} accent={accent} muted={muted} scale={scale} />
          {invoice.paymentTerms && (
            <MetaPair label="Conditions de règlement" value={invoice.paymentTerms} theme={theme} accent={accent} muted={muted} scale={scale} />
          )}
        </div>
      </div>

      {/* Line items */}
      <div style={{ padding: `${12 * scale}px ${56 * scale}px` }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: theme.fontStack,
            fontSize: 12 * scale,
          }}
        >
          <thead>
            <tr style={{ background: theme.bandStyle === 'solid' ? accent : '#F3F3F3', color: theme.bandStyle === 'solid' ? '#FFFFFF' : theme.ink }}>
              <th style={cellTh(scale, 'left')}>DESCRIPTION</th>
              <th style={{ ...cellTh(scale, 'right'), width: 80 * scale }}>QTÉ</th>
              <th style={{ ...cellTh(scale, 'right'), width: 130 * scale }}>
                {invoice.lineItems.some((li) => (li.discountBp ?? 0) > 0) ? 'PRIX U. / REM.' : 'PRIX UNITAIRE'}
              </th>
              <th style={{ ...cellTh(scale, 'right'), width: 110 * scale }}>TOTAL HT</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ ...cellTd(scale), color: muted, textAlign: 'center', padding: 24 * scale }}>
                  Aucun article pour le moment
                </td>
              </tr>
            ) : invoice.lineItems.map((li, i) => {
              const off = (li.discountBp ?? 0) > 0 ? ` −${Math.round((li.discountBp ?? 0) / 100)}%` : '';
              return (
                <tr key={i} style={{ borderBottom: `1px solid ${rule}` }}>
                  <td style={cellTd(scale)}>{li.description || ' '}</td>
                  <td style={{ ...cellTd(scale), textAlign: 'right' }}>{li.quantity ?? 0}</td>
                  <td style={{ ...cellTd(scale), textAlign: 'right' }}>
                    {money(li.unitPrice)}
                    {off && <span style={{ color: muted }}>{off}</span>}
                  </td>
                  <td style={{ ...cellTd(scale), textAlign: 'right' }}>{money(li.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div
        style={{
          padding: `${8 * scale}px ${56 * scale}px ${24 * scale}px`,
          display: 'grid',
          gridTemplateColumns: '1fr 280px',
          gap: 16 * scale,
        }}
      >
        <div />
        <div>
          <TotalsRow label="Sous-total HT" value={`${money(invoice.subtotal)} ${invoice.currency}`} scale={scale} muted={muted} ink={theme.ink} />
          <TotalsRow label="TVA"           value={`${money(invoice.taxTotal)} ${invoice.currency}`} scale={scale} muted={muted} ink={theme.ink} />
          <div style={{ borderTop: `1px solid ${rule}`, margin: `${6 * scale}px 0` }} />
          <TotalsRow label="Total TTC"     value={`${money(invoice.total)} ${invoice.currency}`} scale={scale} muted={muted} ink={accent} bold />
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div style={{ padding: `${0}px ${56 * scale}px ${24 * scale}px` }}>
          <SectionLabel theme={theme} accent={accent} scale={scale}>Notes & Modalités</SectionLabel>
          <div style={{ color: muted, whiteSpace: 'pre-wrap' }}>{invoice.notes}</div>
        </div>
      )}

      <div
        style={{
          padding: `${10 * scale}px ${56 * scale}px ${20 * scale}px`,
          color: muted,
          textAlign: 'center',
          fontSize: 10 * scale,
        }}
      >
        Généré par Ma Facture
      </div>
    </div>
  );
}

/* ---------- subcomponents ---------- */

function SectionLabel({ theme, accent, scale, children }: { theme: InvoiceTheme; accent: string; scale: number; children: React.ReactNode }) {
  return (
    <div
      style={{
        color: accent,
        fontFamily: theme.fontTitleStack,
        fontWeight: 600,
        fontSize: 10 * scale,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        marginBottom: 4 * scale,
      }}
    >
      {children}
    </div>
  );
}

function PartyBlock({ party, muted, scale }: { party: Party; muted: string; scale: number }) {
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 14 * scale }}>{party?.name || ' '}</div>
      {party?.address && <div style={{ color: muted, fontSize: 12 * scale, whiteSpace: 'pre-line' }}>{party.address}</div>}
      {party?.taxId && <div style={{ color: muted, fontSize: 11 * scale }}>N° TVA / SIRET : {party.taxId}</div>}
      {party?.email && <div style={{ color: muted, fontSize: 11 * scale }}>{party.email}</div>}
      {party?.phone && <div style={{ color: muted, fontSize: 11 * scale }}>{party.phone}</div>}
    </div>
  );
}

function MetaPair({ label, value, theme, accent, muted, scale }: { label: string; value: string; theme: InvoiceTheme; accent: string; muted: string; scale: number }) {
  return (
    <div style={{ marginBottom: 8 * scale }}>
      <div
        style={{
          color: accent,
          fontFamily: theme.fontTitleStack,
          fontWeight: 600,
          fontSize: 9.5 * scale,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13 * scale, color: theme.ink }}>{value}</div>
      {void muted}
    </div>
  );
}

function TotalsRow({ label, value, scale, muted, ink, bold }: { label: string; value: string; scale: number; muted: string; ink: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: `${4 * scale}px 0` }}>
      <span style={{ color: muted, fontSize: bold ? 14 * scale : 12 * scale }}>{label}</span>
      <span style={{ color: ink, fontSize: bold ? 18 * scale : 13 * scale, fontWeight: bold ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  );
}

function cellTh(scale: number, align: 'left' | 'right'): React.CSSProperties {
  return {
    textAlign: align,
    padding: `${8 * scale}px ${10 * scale}px`,
    fontSize: 9.5 * scale,
    fontWeight: 600,
    letterSpacing: '0.08em',
  };
}

function cellTd(scale: number): React.CSSProperties {
  return {
    textAlign: 'left',
    padding: `${10 * scale}px ${10 * scale}px`,
    verticalAlign: 'top',
  };
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  if (iso.length === 10) return iso;
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return iso; }
}
